import React from 'react';
import { cn } from '@/lib/cn';
import { IconChevronDown } from '@repo/svgs';

export type FilterChipSize = 'default' | 'large';

export interface FilterChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether the filter is currently active (selected) */
  active?: boolean;
  /** Optional 16px leading icon component */
  leadingIcon?: React.ReactNode;
  /** Optional 16px trailing icon component */
  showTrailingIcon?: boolean;
  /** Visual size: default (12x8) or large (16x12) */
  size?: FilterChipSize;
  /** Label text */
  label: string | React.ReactNode;
  /** Whether the filter is currently open */
  isOpen?: boolean;
  /** Whether the label becomes bold when active. Defaults to true. */
  boldWhenActive?: boolean;
}

export function FilterChip({
  active = false,
  leadingIcon,
  showTrailingIcon = true,
  size = 'default',
  label,
  className,
  isOpen = false,
  boldWhenActive = true,
  disabled,
  ...props
}: FilterChipProps) {
  const padding = size === 'large' ? 'px-4 py-3' : 'px-3 py-2';

  return (
    <button
      type="button"
      disabled={disabled}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      className={cn(
        'box-border flex items-center justify-center gap-2 overflow-clip rounded-full',
        padding,
        'border border-secondary',
        active
          ? 'bg-active hover:bg-active-hover ring-2 ring-accent ring-inset !border-inverted'
          : 'bg-primary hover:bg-primary-hover ',
        'transition-colors',
        disabled && 'opacity-50',
        className,
      )}
      {...props}
    >
      {leadingIcon ? <span className="w-4 h-4 shrink-0">{leadingIcon}</span> : null}
      <span
        className={cn(
          'text-body-2 text-primary whitespace-nowrap',
          active && boldWhenActive && 'font-semibold',
        )}
      >
        {label}
      </span>
      {showTrailingIcon && (
        <span className="w-3 h-3 shrink-0">
          <IconChevronDown
            className={cn(
              'w-full h-full icon-primary transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </span>
      )}
    </button>
  );
}

export default FilterChip;
